import { CheckOutlined, ClockCircleOutlined, GlobalOutlined, MessageOutlined, PhoneOutlined, WalletOutlined } from "@ant-design/icons";
import { Button, Checkbox, List, Skeleton } from "antd";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { _adr } from "../../../address";
import { useDealer } from "../../../tools/api";
import { clearTokenOTP } from "../../../tools/services";
import { complexDigitToman } from "../../../tools/tools";
import { clearFormData } from "../../../reducer/serviceReducer";
import { clearGlobalFormData } from "../../../reducer/serviceReducerGlobala";
import { NotFound } from "../../../sources/svg/svg";
import ShopPage, { ShopSummary, toman } from "../../auxiliary/shop/ShopPage";
import { groupBundles, promoBeforePrice } from "../shop/sp-kit/bundle";
import "./esim.scss";

// same titles as the physical sale: the number and profile pages are shared
export const ESIM_STEPS = [{ title: "بسته آغازین" }, { title: "انتخاب شماره" }, { title: "اطلاعات هویتی" }, { title: "پیش فاکتور" }, { title: "سند" }];

// ponytail: temporary copy of the aptel.ir/esim-conversion terms; replace when the business unit sends the sales terms
const TERMS_ITEMS = [
	"فعال‌سازی eSIM تنها روی دستگاه‌های سازگار با فناوری eSIM امکان‌پذیر است. مسئولیت اطمینان از پشتیبانی دستگاه بر عهده مشترک است.",
	"برای نصب و فعال‌سازی eSIM، اتصال دستگاه به اینترنت بین‌الملل الزامی است.",
	"QR Code یا Activation Code یک‌بار مصرف است و پس از استفاده، امکان استفاده مجدد از آن برای فعال‌سازی پروفایل وجود ندارد.",
	"QR Code یا Activation Code قابل انتقال و استفاده روی دستگاه دیگر نیست.",
	"در صورت حذف پروفایل eSIM، تعویض دستگاه یا نیاز به دریافت مجدد QR Code یا Activation Code، باید مطابق فرآیند اعلام‌شده توسط آپتل درخواست مجدد ثبت شود و تعرفه‌های جاری در زمان درخواست ملاک خواهد بود.",
	"حذف پروفایل eSIM از دستگاه به منزله حذف اطلاعات پروفایل از آن دستگاه است و QR Code قبلی برای نصب مجدد قابل استفاده نخواهد بود.",
	"در صورت تعویض دستگاه، برای انتقال سرویس به دستگاه جدید باید مطابق فرآیند اعلام‌شده توسط آپتل اقدام نمایید.",
	"مسئولیت حفظ و نگهداری QR Code و Activation Code و جلوگیری از دسترسی دیگران به آن، بر عهده مشترک است.",
	"عملکرد eSIM ممکن است به مدل دستگاه، نسخه سیستم‌عامل و تنظیمات دستگاه وابسته باشد.",
	"در صورت بروز مشکل در فعال‌سازی، مشترک می‌تواند مطابق راهنمای فعال‌سازی یا کانال‌های پشتیبانی آپتل اقدام نماید.",
	"با توجه به عدم امکان عودت وجه سرویس eSIM، پیش از اسکن QR Code نسبت به مطالعه قوانین و مقررات اقدام نمایید.",
];

// the API's attributeIcon names are not in this project's icon font: map them to antd icons
const FEATURE_ICONS = {
	"icon-Web": GlobalOutlined,
	"icon-Call": PhoneOutlined,
	"icon-Sms": MessageOutlined,
	"icon-fi-rr-alarm-clock": ClockCircleOutlined,
	"icon-Dolor": WalletOutlined,
};
// one line per feature: every attribute row arrives twice, and the icon-less row (order 20) repeats the internet line,
// so icon-less rows are shown only when a bundle has no other rows
const bundleFeatures = (bundle) => {
	const rows = [bundle, ...(bundle.attribute || [])].filter((row) => row.briefDescription);
	const withIcon = rows.filter((row) => row.attributeIcon);
	const seen = new Set();
	return (withIcon.length ? withIcon : rows).filter((row) => !seen.has(row.attributeOrderId) && seen.add(row.attributeOrderId));
};

// eSIM sale, step 1: terms + bundle, laid out like the package sale (numbered sections, cards, live summary)
const EsimBundle = () => {
	const nav = useNavigate();
	const dispatch = useDispatch();
	const { queryBundleSalesSimByPlanId } = useDealer();
	const [bundles, setBundles] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [accepted, setAccepted] = useState(false);
	const [selected, setSelected] = useState(null);

	// a new sale starts here: drop whatever the previous one left behind
	useEffect(() => {
		dispatch(clearFormData(["spPurchase"]));
		dispatch(clearGlobalFormData(["spPurchase"]));
		clearTokenOTP();
		queryBundleSalesSimByPlanId({ planid: Number(process.env.REACT_APP_PLANID_ESIM) })
			.then((res) => res && setBundles(groupBundles(Array.isArray(res) ? res : res?.spBundle)))
			.finally(() => setIsLoading(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const goMsisdn = () =>
		nav(_adr.Root + _adr.SimCardPurchase.Index + _adr.SimCardPurchase.Msisdn, {
			state: { bundle: selected, kit: "", msisdn: [], iccid: "", isEsim: true },
		});

	const hint = !accepted ? "ابتدا قوانین را تأیید کنید" : !selected ? "یک بسته انتخاب کنید" : null;

	return (
		<ShopPage title="فروش eSIM" prefix="sp" steps={ESIM_STEPS.map((s) => s.title)} step={0}>
			<div className="charge-layout esim-flow">
				<section className="shop-section">
					<h4>
						<span className="num">۱</span>قوانین و مقررات eSIM
					</h4>
					<p className="esim-terms-lead">پیش از ادامه، این موارد را برای خریدار توضیح دهید. وجه سرویس eSIM قابل عودت نیست.</p>
					<ol className="esim-terms" tabIndex={0} aria-label="قوانین و مقررات eSIM">
						{TERMS_ITEMS.map((item) => (
							<li key={item}>{item}</li>
						))}
					</ol>
					<Checkbox className="esim-accept" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}>
						قوانین و مقررات را برای خریدار توضیح دادم و خریدار آن را پذیرفت
					</Checkbox>
				</section>

				<ShopSummary
					wide
					showMsisdn={false}
					title="خلاصه خرید eSIM"
					empty={selected ? null : "یک بسته انتخاب کنید"}
					rows={[
						["بسته", selected?.bundleName],
						["قیمت بسته", toman(selected?.bundlePrice)],
						["قیمت eSIM و مالیات", "در پیش‌فاکتور"],
					]}
					action={
						<Button className="sum-action" size="large" disabled={!!hint} onClick={goMsisdn}>
							{hint || "ادامه و انتخاب شماره"}
						</Button>
					}
				/>

				<section className="shop-section">
					<h4>
						<span className="num">۲</span>انتخاب بسته
					</h4>
					{isLoading ? (
						<Skeleton active paragraph={{ rows: 4 }} />
					) : (
						<div className="show-packages">
							<List
								dataSource={bundles}
								locale={{ emptyText: <NotFound text="بسته‌ای برای فروش eSIM وجود ندارد." /> }}
								renderItem={(item) => {
									const isSelected = selected?.bundleSalesSimId === item.bundleSalesSimId;
									return (
										<div
											className={`pkg-card${isSelected ? " selected" : ""}`}
											key={item.bundleSalesSimId}
											role="button"
											tabIndex={0}
											aria-pressed={isSelected}
											onClick={() => setSelected(item)}
											onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setSelected(item)}>
											<div className="pkg-head">
												<span className="esim-tag">eSIM</span>
												<span className="pkg-billing">{item.bundleType === 1 ? "دائمی" : "اعتباری"}</span>
											</div>
											<h3>{item.bundleName}</h3>
											<ul className="esim-features">
												{bundleFeatures(item).map((row) => {
													const Icon = FEATURE_ICONS[row.attributeIcon] || CheckOutlined;
													return (
														<li key={row.attributeOrderId}>
															<Icon aria-hidden="true" />
															<span>{row.briefDescription}</span>
														</li>
													);
												})}
											</ul>
											<div className="pkg-foot">
												<div className="pkg-price">
													{promoBeforePrice(item) && (
														<span className="old" dir="ltr">
															{complexDigitToman(promoBeforePrice(item))}
														</span>
													)}
													<span className="new">
														<span dir="ltr">{complexDigitToman(item.bundlePrice)}</span> <small>تومان</small>
													</span>
												</div>
												{/* on phones the summary is far above the card: the chosen card continues by itself, like the package sale */}
												{isSelected && accepted ? (
													<Button
														htmlType="button"
														tabIndex={-1}
														onClick={(e) => {
															e.stopPropagation();
															goMsisdn();
														}}>
														ادامه ←
													</Button>
												) : (
													<Button htmlType="button" tabIndex={-1}>
														{isSelected ? "انتخاب شد" : "انتخاب"}
													</Button>
												)}
											</div>
										</div>
									);
								}}
							/>
						</div>
					)}
				</section>
			</div>
		</ShopPage>
	);
};

export default EsimBundle;
